/**
 * Perth Property Pulse — /perth-property-pulse
 * A beautiful, editorial Perth property magazine — with an All Secured trust
 * standard underneath. Luxury real-estate-campaign hero, magazine departments,
 * value-first tone.
 *
 * SEO-first: runtime meta + JSON-LD (WebPage, Organization, FAQPage,
 * BreadcrumbList). The crawlable <h1> keeps the exact SEO text:
 * "Perth Property Intelligence, Beautifully Explained."
 *
 * Phase 1 only: placeholder/demo content with elegant source-attribution
 * footnotes. No invented statistics. Existing All Secured routes, checkout,
 * auth, tracking are NOT touched.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowRight, ShieldCheck, Home, Search, Tag, TrendingUp, Building2, Landmark,
  ScrollText, MapPin, Sparkles, AlertCircle, ExternalLink, Clock, CheckCircle2,
  MessageSquare, Send, Loader2, Calendar,
} from 'lucide-react';
import { API_URL } from '../config';
import { ARTICLES } from '../data/perthArticles';
import '../styles/PerthPropertyPulse.css';

const API = `${API_URL}/api`;
const PAGE_URL = 'https://allsecured.com.au/perth-property-pulse';
const SITE_URL = 'https://allsecured.com.au';

// ----------------------------------------------------------------------------
// IMAGES — warm golden-hour / coastal / home toned (premium AI + lifestyle stock)
// ----------------------------------------------------------------------------
const u = (id, w = 1400) => `https://images.unsplash.com/${id}?auto=format&fit=crop&w=${w}&q=80`;
const PROD = 'https://static.prod-images.emergentagent.com/jobs/8eb1e5b0-8e8c-4e88-832b-2916713b4c7a/images/';
const IMG = {
  hero: `${PROD}8f805bc6ab39e384f2c328595edd52e08854bc00a8ec3acd4c5cfb14dd37c81e.png`,
  garden: `${PROD}2967f44df00267da41feae0a4ce231da3e496d5e41ba8ca5b84e09e1e498ee27.png`,
  interior: `${PROD}0e71e4aec243e87308fb142ecb707965958cdb40d63ca68ce92c8fb0a4efd756.png`,
  coastal: 'https://images.pexels.com/photos/35174505/pexels-photo-35174505.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400',
  art1: 'https://images.pexels.com/photos/35174505/pexels-photo-35174505.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400',
  art2: 'https://images.pexels.com/photos/12281850/pexels-photo-12281850.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400',
  art3: u('photo-1600585154340-be6161a56a0c'),
  art4: `${PROD}2967f44df00267da41feae0a4ce231da3e496d5e41ba8ca5b84e09e1e498ee27.png`,
  art5: u('photo-1568605114967-8130f3a36994'),
  art6: u('photo-1449157291145-7efd050a4d0e'),
};

// ----------------------------------------------------------------------------
// CONTENT (Phase 1 — editorial copy, placeholder data, elegant source labels)
// ----------------------------------------------------------------------------
const PERSONAS = [
  { icon: Home, title: 'For homeowners', body: 'Protect, maintain and gently improve the home you already love.' },
  { icon: Search, title: 'For buyers', body: 'See past the listing to the light, land and street underneath.' },
  { icon: Tag, title: 'For sellers', body: 'Prepare quietly and well, so your home is easy to remember.' },
];

const VALUE = [
  { title: 'Before you buy', body: 'Orientation, light, soil and street — the quiet things a listing photo never shows.' },
  { title: 'Before you sell', body: 'Calm gardens, small repairs and good timing often matter more than a grand gesture.' },
  { title: 'Before you renovate', body: "What the planning fine print, your block and Perth's sun will ask of your plans." },
  { title: 'Before summer arrives', body: 'Shade, seals and reticulation — sorted before the heat settles in.' },
  { title: 'Before you plant', body: 'Waterwise choices that stay lush, matched to sandy soil and a bright sky.' },
  { title: 'Before you trust a headline', body: 'How to read a property story slowly, and find the source beneath the noise.' },
];

const MARKET = [
  { icon: TrendingUp, title: 'House prices', stat: '$920,000', statLabel: 'Median house price', body: 'Where established house values sit across greater Perth, as a rolling 12-month median.', source: 'REIWA', url: 'https://reiwa.com.au/the-wa-market/perth-metro/', asAt: '12 months to May 2026' },
  { icon: Building2, title: 'Units & apartments', stat: '$660,000', statLabel: 'Median unit price', body: 'How apartments and units are tracking against houses and the rental market.', source: 'REIWA', url: 'https://reiwa.com.au/the-wa-market/perth-metro/', asAt: '12 months to May 2026' },
  { icon: Home, title: 'Rental pressure', stat: '$750/wk', statLabel: 'Median house rent', body: 'Houses sit near $750 a week and units near $700 — choice stays tight for renters.', source: 'REIWA', url: 'https://reiwa.com.au/the-wa-market/perth-metro/', asAt: '12 months to May 2026' },
  { icon: Search, title: 'Buyer demand', stat: '~680', statLabel: 'Sales in a single week', body: 'Houses, units and land sold across Perth in the week ending 31 May 2026 — a live read on activity.', source: 'REIWA', url: 'https://reiwa.com.au/the-wa-market/perth-metro/', asAt: 'Week ending 31 May 2026' },
  { icon: ScrollText, title: 'Homes for sale', stat: '5,330', statLabel: 'Listed for sale', body: 'Total houses, units and land advertised across the metro that same week.', source: 'REIWA', url: 'https://reiwa.com.au/the-wa-market/perth-metro/', asAt: 'Week ending 31 May 2026' },
  { icon: Landmark, title: 'Confidence', stat: '+7.8%', statLabel: 'WA dwelling value, quarter', body: 'WA logged one of the nation’s strongest quarterly rises; the mean WA dwelling value reached $1,014,200.', source: 'Australian Bureau of Statistics', url: 'https://www.abs.gov.au/statistics/economy/price-indexes-and-inflation/total-value-dwellings/latest-release', asAt: 'Dec quarter 2025' },
];

const PLANNING = [
  { icon: Building2, title: 'City planning strategy', body: 'How long-term strategies quietly guide where and how Perth grows.' },
  { icon: MapPin, title: 'Zoning & land use', body: 'What zoning really means for what can be built, changed or subdivided.' },
  { icon: Landmark, title: 'Precinct change', body: 'The activity centres and precincts being shaped for renewal.' },
  { icon: TrendingUp, title: 'Density', body: 'How density targets can reshape a streetscape over time.' },
  { icon: ScrollText, title: 'Heritage', body: 'Where heritage listings and overlays touch change and renovation.' },
  { icon: ShieldCheck, title: 'What to check', body: 'A short, calm list of planning things worth confirming first.' },
];

const HOME_NOTES = ['Keeping west-facing rooms cooler', 'Shade, awnings and window treatments', 'Roof colour and heat', 'Outdoor materials that last', 'Coastal corrosion awareness', 'Reticulation and water', 'Preparing gardens for heat', 'Seasonal maintenance checks'];
const GARDEN = ['Drought-tolerant plants', 'Native planting', 'Shade trees', 'Sandy soil considerations', 'Hydrozoning', 'Verge gardens', 'Reticulation checks', 'Mulch and soil protection'];
const BUYER = ['Orientation and afternoon sun', 'Street noise and access', 'Council planning context', 'Drainage and soil', 'Renovation potential', 'Building inspection priorities', 'Nearby infrastructure', 'Climate and garden upkeep'];
const SELLER = ['Street appeal', 'Garden preparation', 'Cooling and comfort', 'Small repairs', 'Photography readiness', 'Decluttering', 'Maintenance records', 'Local market context'];
const SUBURB = ['New transport and infrastructure', 'Streets that are quietly changing', 'Where families are moving', 'Lifestyle and amenity shifts', 'Renewal and density precincts', 'Long-term growth signals'];
const CALENDAR = ['Check reticulation', 'Refresh mulch', 'Review shade protection', 'Clean gutters', 'Inspect outdoor timber', 'Check seals and windows', 'Prepare plants for heat', 'Review home security basics'];
const SOURCE_CATS = ['Market notes', 'Council planning', 'Government policy', 'Climate & weather', 'Waterwise gardening', 'Infrastructure', 'Local news'];
const SOURCE_NAMES = ['REIWA', 'Australian Bureau of Statistics', 'City of Perth', 'WA Government', 'Planning WA', 'Water Corporation', 'Bureau of Meteorology', 'Infrastructure Australia', 'Reputable local news'];
const TRUST_STEPS = ['Read the source', 'Check the date', 'Explain the meaning', 'Label the judgement', 'Update when things change'];
const ASK_EXAMPLES = [
  'What should I look for before buying a home in Perth?',
  'How do I keep a Perth garden alive through summer?',
  'What council planning information is worth reading?',
  'How do I prepare my home before selling?',
  'What are the quiet signs that a suburb is changing?',
];
const TOPICS = ['Buying', 'Selling', 'Renovating', 'Council planning', 'Gardens', 'Summer home care', 'Suburb research', 'Something else'];
const HERO_CARD = ['Market signals', 'Planning notes', 'Summer home care', 'Garden watch', 'Suburb shifts'];

const FAQ = [
  { q: 'Is Perth Property Pulse a property listings site?', a: 'No. It is not a listings site. Perth Property Pulse is a calm, editorial guide to Perth property — helping homeowners, buyers and sellers understand the market, council planning, climate, gardens, maintenance and suburb change in plain English.' },
  { q: 'Where does the information come from?', a: 'Serious notes are designed to be traceable to credible sources such as REIWA, the Australian Bureau of Statistics, WA Government, Water Corporation and the Bureau of Meteorology. Facts are sourced, opinions are labelled, and dates are visible. Placeholder content is clearly marked until verified sources are connected.' },
  { q: 'How is this connected to All Secured?', a: 'Perth Property Pulse is created by All Secured. All Secured helps people protect what matters; Perth Property Pulse helps homeowners understand, maintain and protect one of the most important things they own — their home.' },
  { q: 'Can I ask a property question?', a: 'Yes. You can send a question through Ask the Pulse, covering buying, selling, renovating, council planning, gardens, summer home care or suburb research. The best questions may become future guides for other homeowners.' },
];

// ----------------------------------------------------------------------------
// SEO HOOK (title/desc/schema unchanged)
// ----------------------------------------------------------------------------
const setHeadTag = (selector, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(selector.split('[')[0]);
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  return el;
};
const usePerthSEO = () => {
  useEffect(() => {
    const prevTitle = document.title;
    const title = 'Perth Property Pulse | Perth Property Intelligence for Homeowners & Buyers';
    const description =
      'Perth Property Pulse is a premium Perth property intelligence hub for homeowners, buyers and sellers, covering market trends, council planning, climate, gardens, home care and suburb insights.';
    document.title = title;
    const created = [];
    const ensure = (sel, attrs) => {
      if (!document.head.querySelector(sel)) created.push(sel);
      return setHeadTag(sel, attrs);
    };
    ensure('meta[name="description"]', { name: 'description', content: description });
    ensure('link[rel="canonical"]', { rel: 'canonical', href: `${PAGE_URL}/` });
    ensure('meta[property="og:title"]', { property: 'og:title', content: title });
    ensure('meta[property="og:description"]', { property: 'og:description', content: description });
    ensure('meta[property="og:url"]', { property: 'og:url', content: `${PAGE_URL}/` });
    ensure('meta[property="og:type"]', { property: 'og:type', content: 'website' });
    ensure('meta[property="og:image"]', { property: 'og:image', content: IMG.hero });
    ensure('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
    ensure('meta[name="twitter:title"]', { name: 'twitter:title', content: title });
    ensure('meta[name="twitter:description"]', { name: 'twitter:description', content: description });

    const ldId = 'ppp-jsonld';
    let ld = document.getElementById(ldId);
    if (!ld) {
      ld = document.createElement('script');
      ld.id = ldId;
      ld.type = 'application/ld+json';
      document.head.appendChild(ld);
    }
    ld.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': [
        { '@type': 'Organization', name: 'All Secured', url: `${SITE_URL}/`, description: 'Practical digital safety and cyber privacy education, and property intelligence for Australians.' },
        { '@type': 'WebPage', name: title, description, url: `${PAGE_URL}/`, isPartOf: { '@type': 'WebSite', name: 'All Secured', url: `${SITE_URL}/` }, about: 'Perth property intelligence for homeowners, buyers and sellers' },
        { '@type': 'BreadcrumbList', itemListElement: [{ '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` }, { '@type': 'ListItem', position: 2, name: 'Perth Property Pulse', item: `${PAGE_URL}/` }] },
        { '@type': 'FAQPage', mainEntity: FAQ.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })) },
      ],
    });

    return () => {
      document.title = prevTitle;
      document.getElementById(ldId)?.remove();
      created.forEach((sel) => document.head.querySelector(sel)?.remove());
    };
  }, []);
};

const useScrollReveal = () => {
  useEffect(() => {
    const els = document.querySelectorAll('.ppp-reveal');
    if (!els.length || !('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-in'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' },
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
};

// Reusable pieces ------------------------------------------------------------
const Dept = ({ no, label }) => (
  <div className="ppp-dept">
    <span className="ppp-dept-no ppp-serif" aria-hidden="true">{no}</span>
    <span className="ppp-eyebrow">{label}</span>
  </div>
);

const MiniList = ({ items }) => (
  <div className="ppp-mini-grid">
    {items.map((text) => (
      <div className="ppp-mini ppp-reveal" key={text}>
        <span className="ppp-mini-dot" />
        <span className="ppp-mini-text">{text}</span>
      </div>
    ))}
  </div>
);

const PullQuote = ({ children }) => (
  <section className="ppp-quote-band">
    <div className="ppp-container">
      <blockquote className="ppp-quote ppp-reveal ppp-serif">{children}</blockquote>
    </div>
  </section>
);

const ArticleCard = ({ a, feature }) => {
  const inner = (
    <>
      <div className="ppp-article-img" style={{ backgroundImage: `url(${a.image})` }} role="img" aria-label={a.title}>
        <span className="ppp-article-cat">{a.category}</span>
      </div>
      <div className="ppp-article-body">
        {feature && <span className="ppp-article-kicker">Featured note</span>}
        <h3 className="ppp-article-title">{a.title}</h3>
        <p className="ppp-article-excerpt">{a.excerpt}</p>
        <div className="ppp-article-foot">
          <div className="ppp-article-meta">
            {a.published ? (
              <span className="ppp-source-status ppp-source-ok"><CheckCircle2 size={12} /> Sourced &amp; reviewed</span>
            ) : (
              <span className="ppp-source-status"><AlertCircle size={12} /> Source review required</span>
            )}
            <span className="ppp-read-time"><Clock size={11} style={{ verticalAlign: '-1px', marginRight: 4 }} />{a.readTime}</span>
          </div>
          <span className="ppp-article-btn" aria-hidden="true">
            {a.published ? 'Read the Note' : 'Coming soon'} <ArrowRight size={13} />
          </span>
        </div>
      </div>
    </>
  );

  if (a.published) {
    return (
      <Link
        className={`ppp-article ppp-reveal${feature ? ' ppp-article-feature' : ''}`}
        to={`/perth-property-pulse/articles/${a.slug}`}
        data-testid={`ppp-article-${a.slug}`}
        style={{ textDecoration: 'none' }}
      >
        {inner}
      </Link>
    );
  }

  return (
    <article className={`ppp-article ppp-reveal ppp-article-soon${feature ? ' ppp-article-feature' : ''}`}>
      {inner}
    </article>
  );
};

// ----------------------------------------------------------------------------
// PAGE
// ----------------------------------------------------------------------------
const PerthPropertyPulse = () => {
  usePerthSEO();
  useScrollReveal();

  const pulseRef = useRef(null);
  const askRef = useRef(null);
  const guidesRef = useRef(null);
  const readingRef = useRef(null);
  const scrollTo = (ref) => ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const [form, setForm] = useState({ name: '', email: '', topic: 'Buying', question: '' });
  const [submitting, setSubmitting] = useState(false);
  const [askDone, setAskDone] = useState(false);
  const [askErr, setAskErr] = useState('');
  const updateForm = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submitAsk = async (e) => {
    e?.preventDefault?.();
    setAskErr('');
    if (!form.name.trim()) return setAskErr('A first name is lovely — please add yours.');
    if (!/^\S+@\S+\.\S+$/.test(form.email)) return setAskErr('Please add a valid email.');
    if (!form.question.trim()) return setAskErr('Please add your question.');
    setSubmitting(true);
    try {
      await axios.post(`${API}/property/enquiry`, form);
      setAskDone(true);
    } catch (err) {
      setAskErr(err?.response?.data?.detail || 'Could not send right now. Please try again shortly.');
    } finally {
      setSubmitting(false);
    }
  };

  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [subErr, setSubErr] = useState('');
  const subscribe = async (e) => {
    e?.preventDefault?.();
    setSubErr('');
    if (!/^\S+@\S+\.\S+$/.test(email)) return setSubErr('Please enter a valid email.');
    setSubscribing(true);
    try {
      await axios.post(`${API}/property/subscribe`, { email });
      setSubscribed(true);
    } catch {
      setSubErr("Couldn't save your spot — please try again.");
    } finally {
      setSubscribing(false);
    }
  };

  const fillQuestion = (q) => {
    setForm((f) => ({ ...f, question: q }));
    scrollTo(askRef);
  };

  return (
    <div className="ppp-page" data-testid="ppp-page">
      {/* HEADER */}
      <header className="ppp-header">
        <div className="ppp-header-inner">
          <Link to="/" className="ppp-brand" data-testid="ppp-brand">
            <span className="ppp-brand-pulse" />
            <div className="ppp-brand-text">Perth Property Pulse <span>· by All Secured</span></div>
          </Link>
          <nav className="ppp-header-nav">
            <button className="ppp-header-link" onClick={() => scrollTo(pulseRef)}>The Pulse</button>
            <button className="ppp-header-link" onClick={() => scrollTo(guidesRef)}>Guides</button>
            <button className="ppp-header-cta" onClick={() => scrollTo(askRef)} data-testid="ppp-header-cta">
              Ask the Pulse <ArrowRight size={13} />
            </button>
          </nav>
        </div>
      </header>

      {/* HERO */}
      <section className="ppp-hero" data-testid="ppp-hero">
        <div className="ppp-hero-bg" style={{ backgroundImage: `url(${IMG.hero})` }} role="img" aria-label="A modern Perth home glowing in warm golden-hour light above the coast" />
        <div className="ppp-hero-veil" />
        <div className="ppp-hero-grain" />
        <div className="ppp-hero-inner">
          <div className="ppp-hero-grid">
            <div className="ppp-hero-content">
              <span className="ppp-eyebrow">Perth property, beautifully read</span>
              <div className="ppp-hero-wordmark ppp-serif">Perth Property Pulse</div>
              <p className="ppp-hero-tagline ppp-serif">
                Homes. Streets. Gardens. Plans. Prices. <em>Perth, beautifully read.</em>
              </p>
              <h1 className="ppp-hero-h1">Perth Property Intelligence, Beautifully Explained.</h1>
              <p className="ppp-hero-sub">
                A calm, beautifully sourced property magazine for Perth homeowners, buyers and sellers —
                following the market signals, planning shifts, seasonal home care and suburb stories that
                shape how people live.
              </p>
              <div className="ppp-hero-ctas">
                <button className="ppp-btn-primary" onClick={() => scrollTo(guidesRef)} data-testid="ppp-hero-primary">
                  Start Reading <ArrowRight size={17} />
                </button>
                <button className="ppp-btn-ghost" onClick={() => scrollTo(readingRef)} data-testid="ppp-hero-secondary">
                  Explore the Guides
                </button>
              </div>
              <div className="ppp-hero-trust">
                <ShieldCheck size={16} />
                Created by All Secured for people who want to better understand and protect home.
              </div>
            </div>

            {/* Premium glass card — This Month's Pulse */}
            <aside className="ppp-hero-card ppp-reveal" aria-label="This month's pulse">
              <div className="ppp-hero-card-top">
                <span className="ppp-hero-card-kicker">In this issue</span>
                <h2 className="ppp-hero-card-title ppp-serif">This Month&rsquo;s Pulse</h2>
              </div>
              <ul className="ppp-hero-card-list">
                {HERO_CARD.map((item) => (
                  <li key={item}><span className="ppp-mini-dot" /> {item}</li>
                ))}
              </ul>
              <button className="ppp-hero-card-btn" onClick={() => scrollTo(pulseRef)}>
                Read the Month Ahead <ArrowRight size={15} />
              </button>
            </aside>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="ppp-section" data-testid="ppp-positioning">
        <div className="ppp-container">
          <div className="ppp-section-head ppp-reveal">
            <span className="ppp-eyebrow">Welcome</span>
            <h2 className="ppp-h2">Perth is changing, <em>street by street.</em></h2>
            <p className="ppp-lede">
              We follow the signals worth noticing — the homes, plans, gardens, prices, weather and quiet shifts
              shaping how people live here. No noise. No hard sell. Just a calmer way to read Perth property.
            </p>
          </div>
          <div className="ppp-grid-3">
            {PERSONAS.map(({ icon: Icon, title, body }) => (
              <article className="ppp-card ppp-persona ppp-reveal" key={title}>
                <span className="ppp-card-icon"><Icon size={22} /></span>
                <h3 className="ppp-card-title">{title}</h3>
                <p className="ppp-card-body">{body}</p>
              </article>
            ))}
          </div>
          <blockquote className="ppp-pullquote ppp-reveal ppp-serif">
            A home is rarely just a transaction. It is light, street, soil, season and a long
            decision — <em>and it deserves to be read beautifully.</em>
          </blockquote>
        </div>
      </section>

      {/* VALUE-FIRST */}
      <section className="ppp-section ppp-section-sand" ref={guidesRef} data-testid="ppp-value">
        <div className="ppp-container">
          <div className="ppp-section-head ppp-reveal">
            <span className="ppp-eyebrow">Give first</span>
            <h2 className="ppp-h2">Read This <em>Before You Need Us.</em></h2>
            <p className="ppp-lede">
              Most property pages ask for something too quickly. This one is built to give first — guides,
              checklists, explanations and seasonal notes that help you understand your home and your market
              before you ever make a move.
            </p>
          </div>
          <div className="ppp-grid-3">
            {VALUE.map(({ title, body }, i) => (
              <article className="ppp-value ppp-reveal" key={title}>
                <span className="ppp-value-num ppp-serif">{String(i + 1).padStart(2, '0')}</span>
                <h3 className="ppp-value-title">{title}</h3>
                <p className="ppp-card-body">{body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* 01 — THE PULSE (dark) */}
      <section className="ppp-section ppp-section-marketdark" ref={pulseRef} data-testid="ppp-market">
        <div className="ppp-container">
          <div className="ppp-section-head ppp-reveal">
            <Dept no="01" label="The Pulse" />
            <h2 className="ppp-h2">The Pulse</h2>
            <p className="ppp-lede">
              Perth property does not move in one straight line. Prices, rents, listings, demand and confidence
              shift quietly before they become headlines.
            </p>
          </div>
          <div className="ppp-grid-3">
            {MARKET.map(({ icon: Icon, title, stat, statLabel, body, source, url, asAt }) => (
              <article className="ppp-insight ppp-reveal" key={title}>
                <span className="ppp-card-icon"><Icon size={20} /></span>
                <h3 className="ppp-insight-title">{title}</h3>
                <div className="ppp-insight-stat">
                  <span className="ppp-insight-stat-value ppp-serif">{stat}</span>
                  <span className="ppp-insight-stat-label">{statLabel}</span>
                </div>
                <p className="ppp-insight-body">{body}</p>
                <div className="ppp-footnote">
                  <div className="ppp-footnote-row"><span className="ppp-footnote-label">Source</span><a href={url} target="_blank" rel="noopener noreferrer">{source} <ExternalLink size={10} /></a></div>
                  <div className="ppp-footnote-row"><span className="ppp-footnote-label">Reference period</span><span>{asAt}</span></div>
                  <div className="ppp-footnote-row"><span className="ppp-footnote-label">Status</span><span className="ppp-footnote-verified"><CheckCircle2 size={11} /> Sourced &amp; reviewed</span></div>
                  <p className="ppp-footnote-note">General information only — figures are revised over time.</p>
                </div>
              </article>
            ))}
          </div>
          <div className="ppp-section-disclaimer ppp-reveal">
            <ShieldCheck size={18} />
            <span>Every figure above is drawn from a named source with a reference period. Property data is revised over time and is general information only — not financial, legal or property advice.</span>
          </div>
        </div>
      </section>

      {/* 02 — THE PLANNING DESK */}
      <section className="ppp-section" data-testid="ppp-council">
        <div className="ppp-container">
          <div className="ppp-section-head ppp-reveal">
            <Dept no="02" label="The Planning Desk" />
            <h2 className="ppp-h2">The Fine Print That <em>Shapes a Street.</em></h2>
            <p className="ppp-lede">
              The future of a street is often written in planning language first. We turn the documents into
              plain English.
            </p>
          </div>
          <div className="ppp-grid-3">
            {PLANNING.map(({ icon: Icon, title, body }) => (
              <article className="ppp-card ppp-reveal" key={title}>
                <span className="ppp-card-icon"><Icon size={22} /></span>
                <h3 className="ppp-card-title">{title}</h3>
                <p className="ppp-card-body">{body}</p>
              </article>
            ))}
          </div>
          <div className="ppp-section-actions ppp-reveal">
            <a className="ppp-btn-outline" href="https://www.wa.gov.au/organisation/department-of-planning-lands-and-heritage" target="_blank" rel="noopener noreferrer">
              Read the Source <ExternalLink size={15} />
            </a>
          </div>
          <div className="ppp-section-disclaimer ppp-reveal">
            <AlertCircle size={18} />
            <span>Planning information is general in nature. It's always worth checking with the relevant council, a town planner or a qualified professional before making decisions.</span>
          </div>

          <div className="ppp-comingsoon ppp-reveal" data-testid="ppp-planning-assistant">
            <div className="ppp-comingsoon-text">
              <span className="ppp-comingsoon-badge"><Sparkles size={13} /> Coming soon</span>
              <h3 className="ppp-comingsoon-title">A gentle planning explainer</h3>
              <p className="ppp-comingsoon-body">
                A source-grounded helper, designed to explain council planning documents and local schemes in
                plain, human English — so the fine print feels a little less daunting.
              </p>
            </div>
            <button className="ppp-btn-link" onClick={() => scrollTo(askRef)} data-testid="ppp-register-interest">
              Keep Me Posted <ArrowRight size={15} />
            </button>
          </div>
        </div>
      </section>

      <PullQuote>A good property decision begins <em>before the open home.</em></PullQuote>

      {/* 03 — THE READING ROOM (feature-led) */}
      <section className="ppp-section ppp-section-tint" ref={readingRef} data-testid="ppp-newsroom">
        <div className="ppp-container">
          <div className="ppp-section-head ppp-reveal">
            <Dept no="03" label="The Reading Room" />
            <h2 className="ppp-h2">The Reading Room</h2>
            <p className="ppp-lede">
              Slow, useful reading for Perth property people. Sourced, calm and written to make the market feel
              less noisy.
            </p>
          </div>
          <div className="ppp-reading">
            <ArticleCard a={ARTICLES[0]} feature />
            <div className="ppp-grid-3">
              {ARTICLES.slice(1).map((a) => <ArticleCard a={a} key={a.title} />)}
            </div>
          </div>
        </div>
      </section>

      {/* 04 — THE HOME NOTES (photo) */}
      <section className="ppp-section ppp-section-photo" data-testid="ppp-climate">
        <div className="ppp-section-photo-bg" style={{ backgroundImage: `url(${IMG.interior})` }} role="img" aria-label="Warm afternoon light through a calm Perth home interior" />
        <div className="ppp-section-photo-veil" />
        <div className="ppp-container">
          <div className="ppp-section-head ppp-reveal">
            <Dept no="04" label="The Home Notes" />
            <h2 className="ppp-h2">Perth Homes Live Under a <em>Different Sun.</em></h2>
            <p className="ppp-lede">
              A Perth home asks for different care — more shade, smarter cooling, better planting, and a little
              respect for the sun.
            </p>
          </div>
          <MiniList items={HOME_NOTES} />
        </div>
      </section>

      <PullQuote>The Perth sun changes <em>how a house lives.</em></PullQuote>

      {/* 05 — THE GARDEN EDIT (photo) */}
      <section className="ppp-section ppp-section-photo" data-testid="ppp-garden">
        <div className="ppp-section-photo-bg" style={{ backgroundImage: `url(${IMG.garden})` }} role="img" aria-label="A lush waterwise Australian garden glowing at sunset" />
        <div className="ppp-section-photo-veil" />
        <div className="ppp-container">
          <div className="ppp-section-head ppp-reveal">
            <Dept no="05" label="The Garden Edit" />
            <h2 className="ppp-h2">The Perth Garden That <em>Still Feels Lush.</em></h2>
            <p className="ppp-lede">
              Waterwise does not have to mean bare. The right Perth garden can be resilient, shaded, textured
              and beautiful.
            </p>
          </div>
          <MiniList items={GARDEN} />
          <div className="ppp-section-disclaimer ppp-reveal" style={{ marginTop: 28 }}>
            <AlertCircle size={18} />
            <span>
              Guidance draws on resources such as Water Corporation and WA Government waterwise advice. Plant
              suitability depends on soil, exposure, water and local council rules — always check local guidance
              before planting on verges or public-facing areas.
            </span>
          </div>
        </div>
      </section>

      {/* 06 — THE BUYER'S EYE */}
      <section className="ppp-section" data-testid="ppp-buyer">
        <div className="ppp-container">
          <div className="ppp-section-head ppp-reveal">
            <Dept no="06" label="The Buyer's Eye" />
            <h2 className="ppp-h2">What to Notice <em>Beyond the Listing.</em></h2>
            <p className="ppp-lede">
              The listing shows the rooms. The smarter questions live in the orientation, street, soil, planning
              context and long-term suburb signals.
            </p>
          </div>
          <MiniList items={BUYER} />
        </div>
      </section>

      {/* 07 — THE SELLER'S QUIET ADVANTAGE */}
      <section className="ppp-section ppp-section-tint" data-testid="ppp-seller">
        <div className="ppp-container">
          <div className="ppp-section-head ppp-reveal">
            <Dept no="07" label="The Seller's Quiet Advantage" />
            <h2 className="ppp-h2">Make a Home <em>Easy to Remember.</em></h2>
            <p className="ppp-lede">
              Before a home is marketed, it can be made easier to remember. Presentation is not decoration. It
              is care made visible.
            </p>
          </div>
          <MiniList items={SELLER} />
        </div>
      </section>

      {/* 08 — THE SUBURB WATCH (photo) */}
      <section className="ppp-section ppp-section-photo" data-testid="ppp-suburb">
        <div className="ppp-section-photo-bg" style={{ backgroundImage: `url(${IMG.coastal})` }} role="img" aria-label="A coastal Perth suburb at golden hour" />
        <div className="ppp-section-photo-veil" />
        <div className="ppp-container">
          <div className="ppp-section-head ppp-reveal">
            <Dept no="08" label="The Suburb Watch" />
            <h2 className="ppp-h2">Every Suburb Has a <em>Rhythm.</em></h2>
            <p className="ppp-lede">
              Every suburb has a rhythm — the cafés, schools, streets, transport, trees, blocks, buyers and small
              changes that shape demand.
            </p>
          </div>
          <MiniList items={SUBURB} />
        </div>
      </section>

      <PullQuote>Every suburb has a rhythm. <em>Every home has clues.</em></PullQuote>

      {/* 09 — THIS MONTH AT HOME */}
      <section className="ppp-section" data-testid="ppp-calendar">
        <div className="ppp-container">
          <div className="ppp-section-head ppp-reveal">
            <Dept no="09" label="This Month at Home" />
            <h2 className="ppp-h2">This Month <em>at Home.</em></h2>
            <p className="ppp-lede">
              A seasonal note for the house, garden and small jobs that keep a Perth home feeling cared for.
            </p>
          </div>
          <div className="ppp-calendar ppp-reveal">
            <span className="ppp-calendar-month"><Calendar size={15} /> This month</span>
            <h3 className="ppp-card-title" style={{ fontSize: 24 }}>A few small, seasonal things</h3>
            <div className="ppp-calendar-tasks">
              {CALENDAR.map((task) => (
                <div className="ppp-calendar-task" key={task}>
                  <span className="ppp-calendar-check"><CheckCircle2 size={15} /></span>
                  {task}
                </div>
              ))}
            </div>
            <p className="ppp-footnote-note" style={{ marginTop: 18 }}>
              General guidance only — these gentle reminders rotate with Perth's seasons as the page grows.
            </p>
          </div>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="ppp-section ppp-philosophy" data-testid="ppp-philosophy">
        <div className="ppp-container">
          <div className="ppp-philosophy-inner ppp-reveal">
            <span className="ppp-eyebrow">Our promise</span>
            <h2 className="ppp-h2">A Property Page That <em>Gives First.</em></h2>
            <p className="ppp-lede">
              Most property pages ask too quickly. Perth Property Pulse is built differently. First, it helps. It
              explains the fine print, shares the seasonal reminders, gathers the sources, notices the suburb
              shifts and turns the noise into something calmer.
            </p>
            <p className="ppp-lede" style={{ marginTop: 18 }}>
              Only later, once trust has been earned, should people be asked for anything.
            </p>
          </div>
        </div>
      </section>

      {/* 10 — ASK THE PULSE */}
      <section className="ppp-section ppp-section-navy" ref={askRef} data-testid="ppp-ask">
        <div className="ppp-container">
          <div className="ppp-section-head ppp-reveal">
            <Dept no="10" label="Ask the Pulse" />
            <h2 className="ppp-h2">Ask the Pulse</h2>
            <p className="ppp-lede">
              Send the property question you wish someone would explain clearly. The best questions may become
              future guides.
            </p>
          </div>
          <div className="ppp-ask-grid">
            <div className="ppp-chat ppp-reveal">
              <div className="ppp-chat-head"><MessageSquare size={18} /> Questions worth asking</div>
              {ASK_EXAMPLES.map((q) => (
                <button className="ppp-chat-q" key={q} onClick={() => fillQuestion(q)} data-testid="ppp-example-q">
                  <Search size={15} /> {q}
                </button>
              ))}
            </div>

            <div className="ppp-form ppp-reveal" data-testid="ppp-ask-form-wrap">
              {!askDone ? (
                <>
                  <div className="ppp-form-eyebrow">A question for the Pulse</div>
                  <h3 className="ppp-form-title">Send your question</h3>
                  <form onSubmit={submitAsk}>
                    <div className="ppp-field">
                      <label className="ppp-field-label" htmlFor="ppp-name">Name</label>
                      <input id="ppp-name" className="ppp-input" value={form.name} onChange={updateForm('name')} autoComplete="name" required data-testid="ppp-input-name" />
                    </div>
                    <div className="ppp-field">
                      <label className="ppp-field-label" htmlFor="ppp-email">Email</label>
                      <input id="ppp-email" type="email" className="ppp-input" value={form.email} onChange={updateForm('email')} autoComplete="email" required data-testid="ppp-input-email" />
                    </div>
                    <div className="ppp-field">
                      <label className="ppp-field-label" htmlFor="ppp-topic">Topic</label>
                      <select id="ppp-topic" className="ppp-select" value={form.topic} onChange={updateForm('topic')} data-testid="ppp-input-topic">
                        {TOPICS.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div className="ppp-field">
                      <label className="ppp-field-label" htmlFor="ppp-question">Your question</label>
                      <textarea id="ppp-question" className="ppp-textarea" value={form.question} onChange={updateForm('question')} placeholder="What would you love explained clearly about Perth property?" required data-testid="ppp-input-question" />
                    </div>
                    {askErr && <div className="ppp-form-error" data-testid="ppp-ask-error">{askErr}</div>}
                    <button type="submit" className="ppp-form-submit" disabled={submitting} data-testid="ppp-ask-submit">
                      {submitting ? <><Loader2 size={16} className="ppp-spin" /> Sending…</> : <>Send Your Question <Send size={15} /></>}
                    </button>
                  </form>
                </>
              ) : (
                <div className="ppp-form-success" data-testid="ppp-ask-success">
                  <div className="ppp-form-success-icon"><CheckCircle2 size={30} /></div>
                  <h3 className="ppp-form-title" style={{ marginBottom: 10 }}>Thank you — your question has landed.</h3>
                  <p className="ppp-card-body">We read every one. The best questions may become future guides for other Perth homeowners.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* 11 — OUR SOURCE STANDARD */}
      <section className="ppp-section ppp-section-sand" data-testid="ppp-trust">
        <div className="ppp-container">
          <div className="ppp-section-head ppp-reveal">
            <Dept no="11" label="Our Source Standard" />
            <h2 className="ppp-h2">Beauty Matters. <em>So Does Accuracy.</em></h2>
            <p className="ppp-lede">
              Trust is built slowly. We read the source, check the date, explain the meaning, label the judgement
              and update when things change.
            </p>
          </div>

          <div className="ppp-source-cats ppp-reveal">
            {SOURCE_CATS.map((label) => (
              <div className="ppp-source-cat" key={label}><span className="ppp-mini-dot" /> {label}</div>
            ))}
          </div>

          <div className="ppp-reveal" style={{ marginBottom: 48 }}>
            <span className="ppp-form-eyebrow" style={{ display: 'block', marginBottom: 12 }}>Sources we read</span>
            <div className="ppp-source-pills">
              {SOURCE_NAMES.map((s) => <span className="ppp-source-pill" key={s}>{s}</span>)}
            </div>
          </div>

          <div className="ppp-trust-steps ppp-reveal">
            {TRUST_STEPS.map((step, i) => (
              <div className="ppp-trust-step" key={step}>
                <div className="ppp-trust-num ppp-serif">{i + 1}</div>
                <div className="ppp-trust-label">{step}</div>
              </div>
            ))}
          </div>
          <p className="ppp-lede ppp-reveal" style={{ marginTop: 30, fontSize: 17 }}>
            Facts are sourced. Opinions are labelled. Dates are visible. You always know where something came from.
          </p>
        </div>
      </section>

      {/* FOLLOW THE PULSE */}
      <section className="ppp-section ppp-newsletter" data-testid="ppp-newsletter">
        <div className="ppp-container">
          <div className="ppp-newsletter-inner ppp-reveal">
            <span className="ppp-eyebrow" style={{ justifyContent: 'center' }}>Follow the Pulse</span>
            <h2 className="ppp-h2">Join the <em>Monthly Briefing.</em></h2>
            <p className="ppp-lede">
              Once a month: calm, beautifully explained Perth property reading — market shifts, planning notes,
              home care and the quiet signals of changing suburbs.
            </p>
            {!subscribed ? (
              <form className="ppp-newsletter-form" onSubmit={subscribe}>
                <input type="email" className="ppp-input" placeholder="Enter your email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={subscribing} data-testid="ppp-newsletter-input" />
                <button type="submit" className="ppp-newsletter-btn" disabled={subscribing} data-testid="ppp-newsletter-submit">
                  {subscribing ? <Loader2 size={16} className="ppp-spin" /> : <>Join the Monthly Briefing <ArrowRight size={15} /></>}
                </button>
              </form>
            ) : (
              <div className="ppp-newsletter-success" data-testid="ppp-newsletter-success">
                <CheckCircle2 size={20} /> You're on the list. The next briefing will find you.
              </div>
            )}
            {subErr && <p className="ppp-newsletter-err">{subErr}</p>}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="ppp-footer">
        <div className="ppp-footer-inner">
          <Link to="/" className="ppp-footer-brand">
            <span className="ppp-brand-pulse" /> Perth Property Pulse
          </Link>
          <nav className="ppp-footer-links">
            <Link to="/">All Secured Home</Link>
            <Link to="/privacy-policy">Privacy</Link>
            <Link to="/terms-of-use">Terms</Link>
            <button onClick={() => scrollTo(askRef)}>Ask the Pulse</button>
          </nav>
        </div>
        <p className="ppp-footer-fine">
          Perth Property Pulse is created by All Secured. Everything here is general information only and is not
          financial, legal, planning or property advice. Market figures are drawn from named sources such as REIWA
          and the Australian Bureau of Statistics with the reference periods shown, and are revised over time. Always
          check official sources and qualified professionals before making property decisions.
        </p>
      </footer>
    </div>
  );
};

export default PerthPropertyPulse;
